import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

// Binds a single-dimension sub-tab (or any single-select value) to a URL query
// param so it is shareable / deep-linkable. The param is the source of truth:
// the value comes from the URL, validated against `allowedValues`. When
// `writeDefaultOnEntry` is enabled (the default), the active value is written
// back on entry / when the URL holds an invalid value, so the URL reflects what
// is shown; pass `writeDefaultOnEntry: false` to leave the URL untouched until
// an explicit change. Pass `defaultValue` to prefer a specific allowed value as
// the entry fallback instead of the first allowed one. Pass `isUrlBound: false`
// when the URL belongs to another page (e.g. the host list of an embedded
// preview profile): the value then lives in local state and the URL is never
// read or written. Entity-specific concerns (which param key, how to read it,
// the allowed list) stay with the caller.
export function useQueryParamSubTab({
  allowedValues,
  defaultValue,
  isUrlBound = true,
  paramKey,
  readValue,
  writeDefaultOnEntry = true,
}) {
  const [searchParams, setSearchParams] = useSearchParams()

  const [localValue, setLocalValue] = useState(null)
  const [localEntryValue, setLocalEntryValue] = useState(null)

  // Fall back to `defaultValue` when it is allowed, otherwise the first allowed
  // value, or `null` when there is none (e.g. an empty list during initial
  // loading) so callers never receive `undefined`.
  const fallbackValue =
    defaultValue != null && allowedValues.includes(defaultValue)
      ? defaultValue
      : (allowedValues[0] ?? null)

  // Detached counterpart of the URL write-back below: once the entry default
  // settles (`writeDefaultOnEntry` turns true), pin it so later `defaultValue`
  // changes don't move the tab under the user. State-during-render on a
  // guarded condition, per the React "storing information from previous
  // renders" pattern.
  if (
    !isUrlBound &&
    writeDefaultOnEntry &&
    localValue == null &&
    localEntryValue == null &&
    fallbackValue != null
  ) {
    setLocalEntryValue(fallbackValue)
  }

  const valueFromUrl = readValue
    ? readValue(searchParams)
    : searchParams.get(paramKey)
  const selectedValue = isUrlBound
    ? valueFromUrl
    : (localValue ?? localEntryValue)
  const isValid = selectedValue != null && allowedValues.includes(selectedValue)
  const activeSubTab = isValid ? selectedValue : fallbackValue

  // Hold the latest allowed list in a ref so the setter can validate without
  // changing identity each render (callers may pass a freshly-built array).
  const allowedValuesRef = useRef(allowedValues)

  useEffect(() => {
    allowedValuesRef.current = allowedValues
  }, [allowedValues])

  function writeValueToUrl(value) {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)

        next.set(paramKey, value)

        return next
      },
      { replace: true }
    )
  }

  function setActiveSubTab(value) {
    if (allowedValuesRef.current.includes(value)) {
      if (!isUrlBound) {
        setLocalValue(value)

        return
      }

      writeValueToUrl(value)
    }
  }

  useEffect(() => {
    if (isUrlBound && writeDefaultOnEntry && !isValid && activeSubTab) {
      writeValueToUrl(activeSubTab)
    }
  }, [activeSubTab, isUrlBound, isValid, writeDefaultOnEntry])

  return {
    activeSubTab,
    isValueFromUrl: isValid,
    setActiveSubTab,
  }
}
